import { HttpErrorResponse } from '@angular/common/http';
import {
  BackendDeletePayloadTypes,
  BackendDeleteReturnTypes,
  BackendFetchTypes,
  BackendPushPayloadTypes,
  BackendPushReturnTypes,
  BackendSetPayloadTypes,
  BackendSetReturnTypes,
  BackendUpdatePayloadTypes,
  BackendUpdateReturnTypes,
} from '../backend.model';
import { Collections } from '../collections.model';
import { Reference } from './store.model';

import { WebsocketEvent } from './websocket';

/**
 * List of actions corresponding to the root model.
 * Note: this is an extension of @ngrx/store's Action.
 */
export type CollectionActions<K extends keyof Collections> =
  // | { type: 'API_FETCH'; payload: FetchRequestPayload<T> }
  | { type: 'API_FETCH_SUCCESS'; payload: FetchSuccessPayload<K> }
  | { type: 'API_FETCH_ERROR'; payload: FetchErrorPayload<K> }
  | { type: 'API_PUSH'; payload: PushRequestPayload<K> }
  | { type: 'API_PUSH_SUCCESS'; payload: PushSuccessPayload<K> }
  | { type: 'API_PUSH_ERROR'; payload: PushErrorPayload<K> }
  | { type: 'API_SET'; payload: SetRequestPayload<K> }
  | { type: 'API_SET_SUCCESS'; payload: SetSuccessPayload<K> }
  | { type: 'API_SET_ERROR'; payload: SetErrorPayload<K> }
  | { type: 'API_UPDATE'; payload: UpdateRequestPayload<K> }
  | { type: 'API_UPDATE_SUCCESS'; payload: UpdateSuccessPayload<K> }
  | { type: 'API_UPDATE_ERROR'; payload: UpdateErrorPayload<K> }
  | { type: 'API_DELETE'; payload: DeleteRequestPayload<K> }
  | { type: 'API_DELETE_SUCCESS'; payload: DeleteSuccessPayload<K> }
  | { type: 'API_DELETE_ERROR'; payload: DeleteErrorPayload<K> }
  | { type: 'REQUEST_DATA'; payload: RequestDataPayload<K> }
  | { type: 'UNKNOWN_API_FETCH_ERROR'; payload: FetchErrorPayload<K> }
  | { type: 'WS_MESSAGE'; payload: WebsocketActionPayload };

/**
 * List of actions for every root model.
 */
export type TypedAction = any; // FIXME

interface BasePayload<K extends keyof Collections> {
  type: K; // While this is in the ref, this helps TypeScript do the discriminated union
  ref: Reference<K>;
}

interface BaseErrorPayload {
  message: string;
  error?: HttpErrorResponse;
}

/****************************************
 *  Fetch                               *
 ****************************************/
interface FetchSuccessPayload<K extends keyof Collections>
  extends BasePayload<K> {
  result: BackendFetchTypes[K];
}

interface FetchErrorPayload<K extends keyof Collections>
  extends BasePayload<K> {
  message: string;
}

/****************************************
 *  Push                                *
 ****************************************/
export interface PushRequestPayload<K extends keyof Collections>
  extends BasePayload<K> {
  object: Collections[K];
  rawRequest: BackendPushPayloadTypes[K];
  asFormData?: boolean;
}

export interface PushSuccessPayload<K extends keyof Collections>
  extends BasePayload<K> {
  object: Collections[K];
  rawRequest: BackendPushPayloadTypes[K];
  result: BackendPushReturnTypes[K];
}

type PushErrorPayload<K extends keyof Collections> = PushRequestPayload<K> &
  BaseErrorPayload;

/****************************************
 *  Set                                 *
 ****************************************/

export interface SetRequestPayload<K extends keyof Collections>
  extends BasePayload<K> {
  object: Collections[K];
  originalObject: Collections[K];
  rawRequest: BackendSetPayloadTypes[K]; //  FIXME Do I need method?
  asFormData?: boolean;
}

interface SetSuccessPayload<K extends keyof Collections>
  extends BasePayload<K> {
  object: Collections[K];
  originalObject: Collections[K];
  rawRequest: BackendSetPayloadTypes[K]; //  FIXME Do I need method?
  result: BackendSetReturnTypes[K];
}

type SetErrorPayload<K extends keyof Collections> = SetRequestPayload<K> &
  BaseErrorPayload;

/****************************************
 *  Update                                 *
 ****************************************/

export interface UpdateRequestPayload<K extends keyof Collections>
  extends BasePayload<K> {
  object: Partial<Collections[K]>;
  originalObject: Collections[K];
  rawRequest: BackendUpdatePayloadTypes[K]; //  FIXME Do I need method?
  asFormData?: boolean;
}

interface UpdateSuccessPayload<K extends keyof Collections>
  extends BasePayload<K> {
  object: Partial<Collections[K]>;
  originalObject: Collections[K];
  rawRequest: BackendUpdatePayloadTypes[K]; //  FIXME Do I need method?
  result: BackendUpdateReturnTypes[K];
}

type UpdateErrorPayload<K extends keyof Collections> = UpdateRequestPayload<K> &
  BaseErrorPayload;

/****************************************
 *  Delete                              *
 ****************************************/

export interface DeleteRequestPayload<K extends keyof Collections>
  extends BasePayload<K> {
  originalObject: Collections[K];
  rawRequest: BackendDeletePayloadTypes[K]; //  FIXME Do I need method?
  asFormData?: boolean;
}

interface DeleteSuccessPayload<K extends keyof Collections>
  extends BasePayload<K> {
  originalObject: Collections[K];
  rawRequest: BackendDeletePayloadTypes[K]; //  FIXME Do I need method?
  result: BackendDeleteReturnTypes[K];
}

type DeleteErrorPayload<K extends keyof Collections> = DeleteRequestPayload<K> &
  BaseErrorPayload;

/****************************************
 *  Request data                        *
 ****************************************/

export type RequestDataPayload<K extends keyof Collections> = BasePayload<K>;

/****************************************
 *  Websocket                           *
 ****************************************/

export type WebsocketActionPayload = WebsocketEvent & { toUserId: string };

/****************************************
 *  Type guards                         *
 ****************************************/

export function isRequestDataAction(
  a: TypedAction,
): a is { type: 'REQUEST_DATA'; payload: RequestDataPayload<any> } {
  return a.type === 'REQUEST_DATA';
}

export function isWebsocketAction(
  a: TypedAction,
): a is { type: 'WS_MESSAGE'; payload: WebsocketActionPayload } {
  return a.type === 'WS_MESSAGE';
}

// Appends the collection key to an action's type in order to clarify what part
// of the store the action is affecting. In the case of a websocket message,
// this appends the type of the message.
// Used by @ngrx/store-devtools
export function datastoreActionSanitizer(
  action: TypedAction,
  id: number,
): { type: string; payload: object } {
  if (!action.payload) {
    return action;
  }

  let ids;
  if (action.type !== 'WS_MESSAGE') {
    const path2 = action.payload.ref.path.ids;
    ids = path2 ? path2.join(', ') : undefined;
  }

  return {
    ...action,
    type: `${action.type} [${action.payload.type}] ${ids ? ` - ${ids}` : ''}`,
  };
}



// WEBPACK FOOTER //
// ./src/@freelancer/datastore/core/actions.ts